import { useIsAuthorized } from '@modules/common/components/CanAccess/hooks';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { ChangeDashboardOwnerMenuItem } from '@modules/dashboard/components/DashboardHeaderActions/ChangeDashboardOwnerMenuItem';
import { DuplicateDashboardMenuItem } from '@modules/dashboard/components/DashboardHeaderActions/DuplicateDashboardMenuItem';
import { SetCopyAsDefaultHomeMenuItem } from '@modules/dashboard/components/DashboardHeaderActions/SetCopyAsDefaultHomeMenuItem';
import { trackDashboardEvent } from '@modules/dashboard/helpers';
import { useIsDashboardEditable } from '@modules/dashboard/hooks';
import { useUpdateDashboard } from '@modules/dashboard/hooks/useUpdateDashboard';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import {
  LabelOffRounded,
  TravelExploreRounded,
  ViewComfyRounded,
} from '@mui/icons-material';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import {
  DashboardUseCase,
  PermissionOperation,
  PermissionResource,
  Scope,
} from 'generated/graphql';
import { SuperPermissionResource } from 'v2/core/entities/user';
import { ChangeDashboardDiscoverabilityDialogContent } from '../ChangeDashboardDiscoverabilityDialogContent';
import { RemoveAsTemplateDialogContent } from '../RemoveAsTemplateDialogContent';
import { ArchiveDashboardMenuItem } from './ArchiveDashboardMenuItem';
import { EnterTVModeMenuItem } from './EnterTVModeMenuItem';

interface DashboardHeaderMenuItemsProps {
  closeMenu: () => void;
}

export const DashboardHeaderMenuItems = ({
  closeMenu,
}: DashboardHeaderMenuItemsProps) => {
  const { showSuccessNotification } = useNotificationContext();
  const dashboard = useDashboardStore(({ dashboard }) => dashboard);
  const { updateDashboard } = useUpdateDashboard();
  const { openModal } = useMuiModalContext();

  const canAccessDiscoverability = useIsAuthorized({
    resource: SuperPermissionResource.Discoverability,
    operation: PermissionOperation.Write,
  });

  const canManageTemplates = useIsAuthorized({
    resource: PermissionResource.Template,
    operation: PermissionOperation.Write,
  });

  const isTemplate = dashboard.useCase === DashboardUseCase.Template;
  const editToken = dashboard?.editToken ?? '';
  const shouldShowTemplateMenuItem =
    canManageTemplates && Boolean(dashboard.widgets?.length);

  const isEditable = useIsDashboardEditable();

  if (!isEditable) {
    return (
      <>
        <EnterTVModeMenuItem />
        <DuplicateDashboardMenuItem closeMenu={closeMenu} />
      </>
    );
  }

  const setAsTemplate = async () => {
    if (!canManageTemplates) {
      return;
    }
    closeMenu();
    await updateDashboard({
      updateRequest: {
        id: dashboard.id,
        useCase: DashboardUseCase.Template,
        scope: Scope.Public,
        viewToken: dashboard.viewToken,
        editToken: editToken ?? '',
      },
    });
    showSuccessNotification({
      contentComponent: 'Dashboard has been set as Template.',
      props: {},
    });
    trackDashboardEvent({
      eventName: 'Dashboard set as Template',
      dashboardLink: window.location.href,
    });
  };

  const removeAsTemplate = () => {
    openModal({
      contentComponent: () => (
        <RemoveAsTemplateDialogContent
          dashboard={{ ...dashboard, editToken }}
          skipRefetchTemplateList
        />
      ),
      modalProps: {
        fullWidth: true,
        maxWidth: 'sm',
        PaperProps: {
          elevation: 3,
        },
      },
    });
    closeMenu();
  };

  const changeDiscoverability = () => {
    openModal({
      contentComponent: () => (
        <ChangeDashboardDiscoverabilityDialogContent
          dashboard={{ ...dashboard, editToken }}
        />
      ),
      modalProps: {
        fullWidth: true,
        maxWidth: 'md',
        PaperProps: {
          elevation: 3,
        },
      },
    });
    closeMenu();
  };

  return (
    <>
      {canAccessDiscoverability && (
        <MenuItem onClick={changeDiscoverability}>
          <ListItemIcon>
            <TravelExploreRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>Change Discoverability</ListItemText>
        </MenuItem>
      )}
      <ChangeDashboardOwnerMenuItem closeMenu={closeMenu} />
      {shouldShowTemplateMenuItem && !isTemplate && (
        <MenuItem onClick={setAsTemplate}>
          <ListItemIcon>
            <ViewComfyRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>Set as Template</ListItemText>
        </MenuItem>
      )}
      {shouldShowTemplateMenuItem && isTemplate && (
        <MenuItem onClick={removeAsTemplate}>
          <ListItemIcon>
            <LabelOffRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>Remove as Template</ListItemText>
        </MenuItem>
      )}
      <SetCopyAsDefaultHomeMenuItem closeMenu={closeMenu} />
      <EnterTVModeMenuItem />
      <DuplicateDashboardMenuItem closeMenu={closeMenu} />
      <ArchiveDashboardMenuItem closeMenu={closeMenu} />
    </>
  );
};
